import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { $userUpdatePasswordStore } from '@lib/store';
import { ValidateUtils } from '@shared/utils';
import { BaseInput, Button, ErrorMessage, Modal, Text } from '@shared/widgets';

interface IForm {
  newPassword: string;
  repeatPassword: string;
}
const DEFAULT_VALUES: IForm = { newPassword: '', repeatPassword: '' };

enum StepEnum {
  Change = 'change',
  Confirm = 'confirm',
}

interface IChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileId: string;
}

export const ChangePasswordModal = (props: IChangePasswordModalProps) => {
  const userUpdatePasswordStore = useUnit($userUpdatePasswordStore.store);

  const [step, setStep] = useState<StepEnum>(StepEnum.Change);

  const { control, formState, handleSubmit, watch, getValues, setError, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const newPassword = watch('newPassword');
  const repeatPassword = watch('repeatPassword');

  useEffect(() => {
    if (newPassword === repeatPassword) setError('repeatPassword', { message: '' });
  }, [newPassword]);

  const closeHandler = () => {
    setStep(StepEnum.Change);
    reset(DEFAULT_VALUES);
    props.onClose();
  };
  const submitHandler = () => setStep(StepEnum.Confirm);
  const confirmHandler = async () => {
    const data = getValues();
    await $userUpdatePasswordStore.events.update({
      password: data.newPassword,
      confirmPassword: data.repeatPassword,
      userId: props.profileId,
    });
    closeHandler();
  };

  return (
    <Modal title='Change password' isOpen={props.isOpen} onClose={closeHandler} width='332px'>
      {step === StepEnum.Change && (
        <form className='flex flex-col gap-6' onSubmit={handleSubmit(submitHandler)}>
          <Controller
            name='newPassword'
            control={control}
            rules={{
              required: ValidateUtils.requiredErrorMessage,
              minLength: { value: 8, message: 'Password must be at least 8 characters long' },
              pattern: { value: ValidateUtils.passwordRegex, message: ValidateUtils.passwordErrorMessage },
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput {...field} type='password' placeholder='New password' />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
          <Text>8 characters minimum, at least one number, one capital letter and one special character</Text>
          <Controller
            name='repeatPassword'
            control={control}
            rules={{
              required: ValidateUtils.requiredErrorMessage,
              validate: (value) => value === newPassword || 'Passwords do not match',
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput {...field} type='password' placeholder='Repeat new password' />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
          <Button color='primary' fullWidth type='submit' disabled={formState.isSubmitting}>
            Change password
          </Button>
        </form>
      )}
      {step === StepEnum.Confirm && (
        <div className='flex flex-col gap-6'>
          <Text weight='medium'>Are you sure you want to change the password?</Text>
          <div className='flex gap-4'>
            <Button color='outlined' fullWidth disabled={userUpdatePasswordStore.loading} onClick={() => setStep(StepEnum.Change)}>
              Decline
            </Button>
            <Button color='primary' fullWidth disabled={userUpdatePasswordStore.loading} onClick={confirmHandler}>
              Change
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
